import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Alert } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import {
    ArrowLeft, Save, Plus, Trash2, FileText, Eye, AlertTriangle,
    BookOpen, Code, ListChecks, Settings2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { getApiUrl } from '../../../utils/ApiUtils';
import swal from 'sweetalert';
import FieldLabel, { InfoDot } from '../Common/FieldLabel';
import TplSelect from '../Common/TplSelect';
import TemplatePreviewModal from './TemplatePreviewModal';
import GuidedTemplateForm from './GuidedTemplateForm';
import {
    getBookkeepingTemplate,
    createBookkeepingTemplate,
    updateBookkeepingTemplate,
    toUserErrorMessage,
} from '../../../services/BookkeepingTemplateService';
import './TemplateEditor.css';

/* ------------------------------------------------------------------ *
 * Domain constants — kept in lockstep with the backend authoritative
 * sources:
 *   accountingtemplates/evaluator/bodySchema.js  (types, origins,
 *     input types, VAT rates)
 *   accountingtemplates/services/BookkeepingTemplateService.js
 *     (TEMPLATE_CATEGORIES, PRIMARY_SOURCE_HOSTS / authorities)
 * ------------------------------------------------------------------ */
const COMPANY_TYPE_CODES = ['AB', 'HB', 'EF', 'KB', 'EK', 'IF', 'ST'];
const COMPANY_TYPE_LABELS = {
    AB: 'Aktiebolag',
    HB: 'Handelsbolag',
    EF: 'Enskild firma',
    KB: 'Kommanditbolag',
    EK: 'Ekonomisk förening',
    IF: 'Ideell förening',
    ST: 'Stiftelse',
};
const CHART_VERSIONS = ['BAS2024', 'BAS2026', 'BAS2018K1'];
const ACCOUNTING_REGIMES = [
    { value: 'k1', label: 'K1' },
    { value: 'k2', label: 'K2' },
    { value: 'k3', label: 'K3' },
    { value: null, label: 'Ej tillämpligt' },
];

const TEMPLATE_TYPES = [
    { value: 'income', label: 'Intäkt' },
    { value: 'expense', label: 'Kostnad' },
    { value: 'transfer', label: 'Överföring' },
    { value: 'investment', label: 'Investering' },
    { value: 'tax', label: 'Skatt' },
    { value: 'payroll', label: 'Lön' },
    { value: 'capital_movement', label: 'Kapitalrörelse' },
    { value: 'year_end', label: 'Bokslut' },
    { value: 'advanced', label: 'Avancerad' },
];

const CATEGORIES = [
    { value: 'forsaljning', label: 'Försäljning / intäkter' },
    { value: 'inkop_varor', label: 'Inköp av varor' },
    { value: 'inkop_tjanster', label: 'Inköp av tjänster' },
    { value: 'inventarier', label: 'Inventarier & avskrivningar' },
    { value: 'personal_lon', label: 'Personal & lön' },
    { value: 'bil_transport', label: 'Bil & transport' },
    { value: 'representation', label: 'Möten & representation' },
    { value: 'bank_avgifter', label: 'Bank, ränta & avgifter' },
    { value: 'skatt_moms', label: 'Skatt & moms' },
    { value: 'eget_kapital', label: 'Eget kapital & utdelning' },
    { value: 'utlagg', label: 'Utlägg & privatkonton' },
    { value: 'periodiseringar', label: 'Periodiseringar & bokslut' },
    { value: 'ovrigt', label: 'Övrigt' },
];

const TRANSACTION_ORIGINS = [
    { value: 'domestic', label: 'Inrikes (Sverige)' },
    { value: 'eu_b2b_goods', label: 'EU B2B — varor' },
    { value: 'eu_b2b_services', label: 'EU B2B — tjänster' },
    { value: 'eu_b2c', label: 'EU B2C' },
    { value: 'non_eu_import_goods', label: 'Import av varor (icke-EU)' },
    { value: 'non_eu_services', label: 'Tjänster icke-EU' },
    { value: 'export', label: 'Export' },
    { value: 'na', label: 'Saknas / ej tillämpligt' },
];

// Input types with a plain-Swedish label and a one-line explanation
// shown beneath the type picker so a novice author understands which
// to choose.
const INPUT_TYPES = [
    { value: 'currency_gross', label: 'Belopp inkl. moms', hint: 'Användaren skriver bruttobelopp; mallen kan använda .gross, .net och .vat.' },
    { value: 'currency_net', label: 'Belopp exkl. moms', hint: 'Användaren skriver nettobelopp; mallen kan använda .gross, .net och .vat.' },
    { value: 'currency', label: 'Belopp (utan moms­uppdelning)', hint: 'Ett rent kronbelopp utan moms­beräkning.' },
    { value: 'number', label: 'Tal (decimal)', hint: 'Ett decimaltal, t.ex. en andel eller kurs.' },
    { value: 'integer', label: 'Heltal', hint: 'Ett heltal, t.ex. antal personer eller en momssats.' },
    { value: 'text', label: 'Text', hint: 'Fritext, t.ex. en notering.' },
    { value: 'date', label: 'Datum', hint: 'Ett datum.' },
    { value: 'account_picker', label: 'Kontoväljare', hint: 'Användaren väljer ett konto ur kontoplanen. Kan filtreras per kontoklass.' },
    { value: 'select', label: 'Listval', hint: 'Användaren väljer ett av flera fördefinierade alternativ.' },
    { value: 'eu_vat_number', label: 'EU-momsnummer', hint: 'Ett VAT-nummer från ett annat EU-land.' },
];

const VAT_RATES = [0, 6, 12, 25];
const VAT_RATE_OPTIONS = VAT_RATES.map((r) => ({ value: r, label: `${r} %` }));
const FACET_TYPES = ['currency_gross', 'currency_net'];

// The Källor (source-citation) section is hidden in the UI for now.
// The citation data still round-trips on the model — it is kept for a
// future AI-assisted use — flip this to true to surface the editor.
const SHOW_CITATIONS = false;
const NUMERIC_TYPES = ['currency_gross', 'currency_net', 'currency', 'number', 'integer'];

const ACCOUNT_FILTER_OPTIONS = [
    { value: '', label: 'Alla konton' },
    { value: 'class:1', label: 'Klass 1 — Tillgångar' },
    { value: 'class:2', label: 'Klass 2 — Eget kapital & skulder' },
    { value: 'class:3', label: 'Klass 3 — Intäkter' },
    { value: 'class:4', label: 'Klass 4 — Material & varor' },
    { value: 'class:5', label: 'Klass 5 — Övriga externa kostnader' },
    { value: 'class:6', label: 'Klass 6 — Övriga externa kostnader' },
    { value: 'class:7', label: 'Klass 7 — Personalkostnader' },
    { value: 'class:8', label: 'Klass 8 — Finansiella poster' },
    { value: 'class:4-6', label: 'Klass 4–6 — Kostnader' },
    { value: 'class:4-7', label: 'Klass 4–7 — Kostnader' },
    { value: 'class:5-6', label: 'Klass 5–6 — Externa kostnader' },
];

// Map the tenant chart of accounts into { value, label } options for
// TplSelect. react-select makes these long lists searchable.
function toAccountOptions(accounts) {
    return accounts.map((a) => ({
        value: a.accountNumber,
        label: `${a.accountNumber} — ${a.accountName}`,
    }));
}

const AUTHORITIES = [
    { value: 'BFN', label: 'BFN — Bokföringsnämnden' },
    { value: 'Skatteverket', label: 'Skatteverket' },
    { value: 'Riksdagen', label: 'Riksdagen (lagtext)' },
    { value: 'Bolagsverket', label: 'Bolagsverket' },
    { value: 'BAS', label: 'BAS-kontoplanen' },
    { value: 'Lagrummet', label: 'Lagrummet' },
    { value: 'Taxonomier', label: 'Taxonomier för årsredovisning' },
    { value: 'Regeringen', label: 'Regeringskansliet' },
];

let _uidCounter = 0;
function uid() {
    _uidCounter += 1;
    return `r${Date.now().toString(36)}${_uidCounter}`;
}

// Sanitize a free-typed input name into a valid DSL identifier
// (^[a-zA-Z_][a-zA-Z0-9_]*$ on the backend).
function sanitizeIdentifier(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9_]/gi, '_')
        .replace(/^[0-9]/, '_');
}

/* ------------------------------------------------------------------ *
 * Body <-> form-state translation. The form state is a near-mirror of
 * the body shape; the only divergences are UI bookkeeping fields
 * prefixed `_` (a stable React key, the account-mode discriminator,
 * the band-map represented as an array). serializeBody() strips all of
 * them so the payload is exactly the schema shape the backend expects.
 * ------------------------------------------------------------------ */
function lineToFormRow(line) {
    let mode = 'account';
    if (line.role) mode = 'role';
    else if (line.account_ref) mode = 'account_ref';
    else if (line.account_band) mode = 'account_band';
    const bandMap = line.account_band && line.account_band.map
        ? Object.entries(line.account_band.map).map(([key, account]) => ({ _id: uid(), key, account }))
        : [{ _id: uid(), key: '', account: '' }];
    return {
        _id: uid(),
        _mode: mode,
        account: line.account || '',
        role: line.role || '',
        account_ref: line.account_ref || '',
        band_from: (line.account_band && line.account_band.from) || '',
        band_map: bandMap,
        side: line.side || 'auto',
        amount: line.amount || '',
        condition: line.condition || '',
        transaction_description: line.transaction_description || '',
    };
}

function inputToFormRow(input) {
    return {
        _id: uid(),
        name: input.name || '',
        label: input.label || '',
        type: input.type || 'currency',
        required: input.required !== false,
        example: input.example !== undefined ? input.example : '',
        default: input.default,
        filter: input.filter || '',
        options: Array.isArray(input.options)
            ? input.options.map((o) => ({ _id: uid(), value: o.value, label: o.label }))
            : [],
        vat_rate: input.vat_rate !== undefined ? input.vat_rate : '',
    };
}

function bodyToFormState(body) {
    const b = body && typeof body === 'object' ? body : {};
    return {
        inputs: Array.isArray(b.inputs) && b.inputs.length
            ? b.inputs.map(inputToFormRow)
            : [],
        lines: Array.isArray(b.lines) && b.lines.length
            ? b.lines.map(lineToFormRow)
            : [newLineRow('debit'), newLineRow('credit')],
        vatReportCodes: (b.vat_report_codes && typeof b.vat_report_codes === 'object') ? b.vat_report_codes : null,
        requiresSupplierVat: !!b.requires_supplier_vat_number,
        requiresCustomerVat: !!b.requires_customer_vat_number,
    };
}

function newInputRow() {
    return {
        _id: uid(), name: '', label: '', type: 'currency_gross', required: true,
        example: '', default: undefined, filter: '', options: [], vat_rate: 25,
        _expanded: true,
    };
}
function newLineRow(side = 'debit') {
    return {
        _id: uid(), _mode: 'account', account: '', role: '', account_ref: '', band_from: '',
        band_map: [{ _id: uid(), key: '', account: '' }],
        side, amount: '', condition: '', transaction_description: '',
    };
}

// Build the body JSON from form state. Only schema fields are emitted —
// `_`-prefixed UI fields and empty optionals are dropped.
//
// Note: no `vat` object is emitted. VAT is driven entirely per-input
// (currency_gross/currency_net + each input's own vat_rate); the
// evaluator never reads body.vat, so the backend schema default for it
// is harmless and we don't surface it in the editor.
function serializeBody({ type, transactionOrigin, vatReportCodes,
    requiresSupplierVat, requiresCustomerVat, inputs, lines }) {
    const body = {
        type,
        transaction_origin: transactionOrigin,
        inputs: inputs.map((i) => {
            const out = {
                name: i.name,
                label: i.label,
                type: i.type,
                required: !!i.required,
            };
            if (i.example !== '' && i.example !== undefined) out.example = coerceExampleValue(i);
            if (i.default !== undefined && i.default !== '') out.default = i.default;
            if (i.type === 'account_picker' && i.filter) out.filter = i.filter;
            if (i.type === 'select') {
                out.options = i.options
                    .filter((o) => o.value !== '' && o.label !== '')
                    .map((o) => ({ value: o.value, label: o.label }));
            }
            if (FACET_TYPES.includes(i.type) && i.vat_rate !== '' && i.vat_rate !== undefined) {
                out.vat_rate = i.vat_rate;
            }
            return out;
        }),
        lines: lines.map((l) => {
            const out = { side: l.side, amount: (l.amount || '').trim() };
            if (l._mode === 'account') out.account = (l.account || '').trim();
            else if (l._mode === 'role') out.role = (l.role || '').trim();
            else if (l._mode === 'account_ref') out.account_ref = l.account_ref;
            else {
                out.account_band = {
                    from: l.band_from,
                    map: l.band_map.reduce((acc, m) => {
                        if (m.key !== '' && m.account !== '') acc[String(m.key)] = String(m.account);
                        return acc;
                    }, {}),
                };
            }
            if (l.condition && l.condition.trim()) out.condition = l.condition.trim();
            if (l.transaction_description && l.transaction_description.trim()) {
                out.transaction_description = l.transaction_description.trim();
            }
            return out;
        }),
    };
    if (vatReportCodes && Object.keys(vatReportCodes).length > 0) {
        body.vat_report_codes = vatReportCodes;
    }
    if (requiresSupplierVat) body.requires_supplier_vat_number = true;
    if (requiresCustomerVat) body.requires_customer_vat_number = true;
    return body;
}

// Coerce a typed-in example to the right primitive for its input type
// (number widgets store strings while typing).
function coerceExampleValue(input) {
    const v = input.example;
    if (NUMERIC_TYPES.includes(input.type)) {
        const n = Number(v);
        return Number.isFinite(n) ? n : v;
    }
    return v;
}

/* ================================================================== *
 * ExpressionField — a text input for amount/condition formulas. The
 * input doubles as its own picker: clicking/focusing it opens a menu
 * of every declared parameter (currency parameters expose their
 * .gross/.net/.vat facets) plus the supported functions, so a novice
 * never has to remember the token names. The menu is portaled to the
 * body so it is never clipped by a table or section.
 * ================================================================== */
function ExpressionField({ value, onChange, inputs, placeholder, allowFacets = true, disabled = false }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const fieldRef = useRef(null);
    const menuRef = useRef(null);

    const tokens = useMemo(() => {
        const list = [];
        for (const i of inputs) {
            if (!i.name) continue;
            if (allowFacets && FACET_TYPES.includes(i.type)) {
                list.push({ token: `${i.name}.gross`, label: `${i.label || i.name} — bruttobelopp` });
                list.push({ token: `${i.name}.net`, label: `${i.label || i.name} — nettobelopp` });
                list.push({ token: `${i.name}.vat`, label: `${i.label || i.name} — moms` });
            } else {
                list.push({ token: i.name, label: i.label || i.name });
            }
        }
        return list;
    }, [inputs, allowFacets]);

    const openMenu = () => {
        if (disabled) return;
        const el = fieldRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 260) });
        setMenuOpen(true);
    };

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => {
            if (fieldRef.current && fieldRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setMenuOpen(false);
        };
        const onScroll = () => setMenuOpen(false);
        document.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [menuOpen]);

    // Insert a token at the caret. onMouseDown (not onClick) on the
    // menu item + preventDefault keeps the input focused so the caret
    // position is still valid and the menu stays open for more inserts.
    const insert = (token) => {
        const el = fieldRef.current;
        const cur = value || '';
        let caret = cur.length;
        if (el && typeof el.selectionStart === 'number') {
            const s = el.selectionStart;
            const e = el.selectionEnd;
            onChange(cur.slice(0, s) + token + cur.slice(e));
            caret = s + token.length;
        } else {
            onChange(cur + token);
        }
        requestAnimationFrame(() => {
            if (fieldRef.current) {
                fieldRef.current.focus();
                try { fieldRef.current.setSelectionRange(caret, caret); } catch { /* noop */ }
            }
        });
    };

    return (
        <div className="tpl-expr">
            <input
                ref={fieldRef}
                type="text"
                className="tpl-input tpl-mono"
                value={value || ''}
                placeholder={placeholder}
                spellCheck={false}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                onFocus={openMenu}
                onClick={openMenu}
                onKeyDown={(e) => { if (e.key === 'Escape') setMenuOpen(false); }}
            />
            {menuOpen && menuPos && createPortal(
                <div className="tpl-expr-menu" ref={menuRef}
                    style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}>
                    <div className="tpl-expr-menu-group">Parametrar</div>
                    {tokens.length === 0 && (
                        <div className="tpl-expr-menu-empty">Inga parametrar definierade ännu.</div>
                    )}
                    {tokens.map((t) => (
                        <button type="button" key={t.token} className="tpl-expr-menu-item"
                            onMouseDown={(e) => { e.preventDefault(); insert(t.token); }}>
                            <code>{t.token}</code>
                            <span>{t.label}</span>
                        </button>
                    ))}
                    <div className="tpl-expr-menu-group">Funktioner</div>
                    {['min(', 'max(', 'round(', 'abs(', 'floor(', 'ceil('].map((fn) => (
                        <button type="button" key={fn} className="tpl-expr-menu-item"
                            onMouseDown={(e) => { e.preventDefault(); insert(fn); }}>
                            <code>{fn})</code>
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </div>
    );
}

/* ================================================================== *
 * ParameterRow — one body.inputs[] entry, rendered as a table row.
 * Type-specific settings (momssats / kontofilter / listval-alternativ)
 * live in an expandable detail row so the table stays compact.
 * ================================================================== */
const paramHasDetail = (type) => FACET_TYPES.includes(type)
    || type === 'account_picker' || type === 'select';

function ParameterRow({ input, onChange, onRemove }) {
    const set = (patch) => onChange({ ...input, ...patch });
    const hasDetail = paramHasDetail(input.type);
    const expanded = hasDetail && !!input._expanded;
    return (
        <>
            <tr className="tpl-trow">
                <td>
                    <input type="text" className="tpl-input tpl-mono" value={input.name}
                        placeholder="t.ex. belopp"
                        onChange={(e) => set({ name: sanitizeIdentifier(e.target.value) })} />
                </td>
                <td>
                    <input type="text" className="tpl-input" value={input.label}
                        placeholder="t.ex. Belopp inkl. moms"
                        onChange={(e) => set({ label: e.target.value })} />
                </td>
                <td>
                    <TplSelect options={INPUT_TYPES} value={input.type}
                        onChange={(v) => set({ type: v, _expanded: paramHasDetail(v) })} />
                </td>
                <td>
                    <ExampleInput input={input} onChange={(example) => set({ example })} />
                </td>
                <td className="tpl-td-center">
                    <input type="checkbox" checked={!!input.required}
                        aria-label="Obligatorisk parameter"
                        onChange={(e) => set({ required: e.target.checked })} />
                </td>
                <td className="tpl-td-actions">
                    {hasDetail && (
                        <button type="button" className="tpl-icon-btn"
                            title={expanded ? 'Dölj inställningar' : 'Visa fler inställningar'}
                            onClick={() => set({ _expanded: !expanded })}>
                            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                    )}
                    <button type="button" className="tpl-icon-btn danger"
                        title="Ta bort parameter" onClick={onRemove}>
                        <Trash2 size={15} />
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr className="tpl-trow-detail">
                    <td colSpan={6}>
                        <div className="tpl-detail">
                            {FACET_TYPES.includes(input.type) && (
                                <div className="tpl-field tpl-detail-field">
                                    <FieldLabel text="Momssats"
                                        hint="Styr hur beloppets brutto-, netto- och momsdel räknas ut." />
                                    <VatRateSelect value={input.vat_rate}
                                        onChange={(vat_rate) => set({ vat_rate })} />
                                </div>
                            )}
                            {input.type === 'account_picker' && (
                                <div className="tpl-field tpl-detail-field">
                                    <FieldLabel text="Kontofilter (valfritt)"
                                        hint="Begränsar vilka konton användaren kan välja, t.ex. bara kostnadskonton." />
                                    <TplSelect options={ACCOUNT_FILTER_OPTIONS} value={input.filter || ''}
                                        onChange={(v) => set({ filter: v })} />
                                </div>
                            )}
                            {input.type === 'select' && (
                                <OptionsEditor options={input.options}
                                    defaultValue={input.default}
                                    onChange={(options) => set({ options })}
                                    onDefaultChange={(d) => set({ default: d })} />
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// Example value widget — matches the input type so the author types a
// value in the right shape.
function ExampleInput({ input, onChange }) {
    if (NUMERIC_TYPES.includes(input.type)) {
        return (
            <input type="number" className="tpl-input" value={input.example}
                step={input.type === 'integer' ? '1' : '0.01'}
                placeholder="t.ex. 1250"
                onChange={(e) => onChange(e.target.value)} />
        );
    }
    if (input.type === 'date') {
        return (
            <input type="date" className="tpl-input" value={input.example || ''}
                onChange={(e) => onChange(e.target.value)} />
        );
    }
    if (input.type === 'select') {
        return (
            <TplSelect
                options={input.options.map((o) => ({ value: o.value, label: o.label || o.value }))}
                value={input.example || ''}
                placeholder="— välj —"
                onChange={onChange} />
        );
    }
    return (
        <input type="text" className="tpl-input" value={input.example || ''}
            placeholder={input.type === 'account_picker' ? 't.ex. 5410'
                : input.type === 'eu_vat_number' ? 't.ex. DE123456789' : 'Exempelvärde'}
            onChange={(e) => onChange(e.target.value)} />
    );
}

function VatRateSelect({ value, onChange }) {
    // The evaluator drives VAT solely from this per-input rate — there
    // is no template-level fallback — so the choice is a plain literal
    // 0 / 6 / 12 / 25 %.
    return (
        <TplSelect
            options={VAT_RATE_OPTIONS}
            value={value}
            placeholder="Välj momssats…"
            onChange={(v) => onChange(Number(v))} />
    );
}

function OptionsEditor({ options, defaultValue, onChange, onDefaultChange }) {
    const setOpt = (id, patch) => onChange(options.map((o) => (o._id === id ? { ...o, ...patch } : o)));
    const add = () => onChange([...options, { _id: uid(), value: '', label: '' }]);
    const remove = (id) => onChange(options.filter((o) => o._id !== id));
    return (
        <div className="tpl-field">
            <FieldLabel text="Alternativ"
                hint="De val användaren kan göra i listrutan. Värdet används i mallen, texten visas för användaren." />
            {options.length === 0 && (
                <small className="tpl-hint">Lägg till minst ett alternativ användaren kan välja.</small>
            )}
            {options.map((o) => (
                <div key={o._id} className="tpl-option-row">
                    <input type="text" className="tpl-input tpl-mono" value={o.value}
                        placeholder="värde (t.ex. 1930)"
                        onChange={(e) => setOpt(o._id, { value: e.target.value })} />
                    <input type="text" className="tpl-input" value={o.label}
                        placeholder="visad text"
                        onChange={(e) => setOpt(o._id, { label: e.target.value })} />
                    <label className="tpl-radio" title="Förvalt alternativ">
                        <input type="radio" checked={defaultValue === o.value}
                            onChange={() => onDefaultChange(o.value)} />
                        Förvalt
                    </label>
                    <button type="button" className="tpl-icon-btn danger"
                        onClick={() => remove(o._id)}>
                        <Trash2 size={14} />
                    </button>
                </div>
            ))}
            <button type="button" className="tpl-btn-outline sm" onClick={add}>
                <Plus size={14} /> Lägg till alternativ
            </button>
        </div>
    );
}

/* ================================================================== *
 * LineRow — one body.lines[] entry, rendered as a table row. Optional
 * fields (villkor, radtext) and the kontoband value-map live in an
 * expandable detail row so the table stays compact.
 * ================================================================== */
const ACCOUNT_MODE_OPTIONS = [
    { value: 'account', label: 'Fast konto' },
    { value: 'role', label: 'Semantisk roll' },
    { value: 'account_ref', label: 'Från parameter' },
    { value: 'account_band', label: 'Kontoband' },
];

function LineRow({ line, inputs, accounts, onChange, onRemove }) {
    const set = (patch) => onChange({ ...line, ...patch });
    // Inputs usable as an account reference: account_picker / select.
    const refInputs = inputs.filter((i) => i.type === 'account_picker' || i.type === 'select');
    // Inputs usable as a band selector: integer / select / number.
    const bandInputs = inputs.filter((i) => ['integer', 'number', 'select'].includes(i.type));
    const accountOptions = useMemo(() => toAccountOptions(accounts), [accounts]);
    const expanded = !!line._expanded;

    // The amount formula belongs to one side. Both Debet and Kredit
    // are real inputs; once one carries a formula the other greys out
    // (a verifikat row can't be both). Clear the active one to switch
    // sides. `auto` (the system picks the side at posting time) can't
    // sit under one heading — it spans both, with an Auto tag.
    const hasAmount = !!(line.amount && String(line.amount).trim());

    return (
        <>
            <tr className="tpl-trow">
                <td>
                    <TplSelect options={ACCOUNT_MODE_OPTIONS} value={line._mode}
                        onChange={(v) => set({
                            _mode: v,
                            _expanded: v === 'account_band' ? true : line._expanded,
                        })} />
                </td>
                <td>
                    {line._mode === 'account' && (
                        <TplSelect options={accountOptions} value={line.account || ''}
                            placeholder="Sök konto…"
                            onChange={(v) => set({ account: v })} />
                    )}
                    {line._mode === 'role' && (
                        <input type="text" className="tpl-input" value={line.role || ''}
                            placeholder="t.ex. primary_bank"
                            aria-label="Semantisk kontoroll"
                            onChange={(e) => set({ role: e.target.value })} />
                    )}
                    {line._mode === 'account_ref' && (
                        <TplSelect
                            options={refInputs.map((i) => ({ value: i.name, label: i.label || i.name }))}
                            value={line.account_ref || ''}
                            placeholder="— välj parameter —"
                            onChange={(v) => set({ account_ref: v })} />
                    )}
                    {line._mode === 'account_band' && (
                        <TplSelect
                            options={bandInputs.map((i) => ({ value: i.name, label: i.label || i.name }))}
                            value={line.band_from || ''}
                            placeholder="— styrparameter —"
                            onChange={(v) => set({ band_from: v })} />
                    )}
                </td>
                {line.side === 'auto' ? (
                    <td colSpan={2} className="tpl-amount-cell">
                        <div className="tpl-amount-auto">
                            <span className="tpl-auto-tag"
                                title="Systemet väljer debet eller kredit utifrån konto och malltyp">
                                Auto
                            </span>
                            <ExpressionField value={line.amount} inputs={inputs}
                                placeholder="t.ex. belopp.net"
                                onChange={(amount) => set({ amount })} />
                        </div>
                    </td>
                ) : (
                    <>
                        <td className="tpl-amount-cell">
                            <ExpressionField
                                value={line.side === 'debit' ? line.amount : ''}
                                disabled={line.side === 'credit' && hasAmount}
                                inputs={inputs}
                                placeholder="t.ex. belopp.net"
                                onChange={(v) => set({ side: 'debit', amount: v })} />
                        </td>
                        <td className="tpl-amount-cell">
                            <ExpressionField
                                value={line.side === 'credit' ? line.amount : ''}
                                disabled={line.side === 'debit' && hasAmount}
                                inputs={inputs}
                                placeholder="t.ex. belopp.net"
                                onChange={(v) => set({ side: 'credit', amount: v })} />
                        </td>
                    </>
                )}
                <td className="tpl-td-actions">
                    <button type="button" className="tpl-icon-btn"
                        title={expanded ? 'Dölj fler inställningar' : 'Visa auto, villkor och radtext'}
                        onClick={() => set({ _expanded: !expanded })}>
                        {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                    <button type="button" className="tpl-icon-btn danger"
                        title="Ta bort rad" onClick={onRemove}>
                        <Trash2 size={15} />
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr className="tpl-trow-detail">
                    <td colSpan={5}>
                        <div className="tpl-detail">
                            {line._mode === 'account_band' && (
                                <BandMapEditor line={line} bandInputs={bandInputs}
                                    accounts={accounts} onChange={set} />
                            )}
                            <label className="tpl-check tpl-auto-toggle">
                                <input type="checkbox" checked={line.side === 'auto'}
                                    onChange={(e) => set({ side: e.target.checked ? 'auto' : 'debit' })} />
                                <span>Låt systemet välja debet/kredit automatiskt</span>
                                <InfoDot hint="Slå på detta när kontot bestäms av en parameter eller ett kontoband — då kan sidan inte avgöras förrän verifikatet skapas. Systemet väljer sida utifrån kontoklass och malltyp. När det är avslaget bestämmer du sidan genom att skriva beloppet i debet- eller kreditkolumnen." />
                            </label>
                            <div className="tpl-grid-2">
                                <div className="tpl-field">
                                    <FieldLabel text="Villkor (valfritt)"
                                        hint="Raden tas bara med när villkoret är sant. Lämna tomt för att alltid ta med raden." />
                                    <ExpressionField value={line.condition} inputs={inputs}
                                        placeholder="t.ex. belopp.vat > 0"
                                        onChange={(condition) => set({ condition })} />
                                </div>
                                <div className="tpl-field">
                                    <FieldLabel text="Radtext (valfritt)"
                                        hint="Texten som hamnar på verifikatraden. Lämna tomt för mallens standardtext." />
                                    <input type="text" className="tpl-input"
                                        value={line.transaction_description}
                                        placeholder="t.ex. Inköp av varor"
                                        onChange={(e) => set({ transaction_description: e.target.value })} />
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// Kontoband value→account map editor — shown in a line's detail row
// when the line resolves its account via a band.
function BandMapEditor({ line, bandInputs, accounts, onChange }) {
    const setMap = (id, patch) => onChange({
        band_map: line.band_map.map((m) => (m._id === id ? { ...m, ...patch } : m)),
    });
    const addMap = () => onChange({ band_map: [...line.band_map, { _id: uid(), key: '', account: '' }] });
    const removeMap = (id) => onChange({ band_map: line.band_map.filter((m) => m._id !== id) });
    const accountOptions = useMemo(() => toAccountOptions(accounts), [accounts]);
    return (
        <div className="tpl-field tpl-band">
            {bandInputs.length === 0 && (
                <small className="tpl-hint">
                    Lägg först till en parameter av typen Heltal, Tal eller Listval — den styr bandet.
                </small>
            )}
            <FieldLabel text="Värde → konto"
                hint="Varje möjligt värde på styrparametern kopplas till ett konto." />
            {line.band_map.map((m) => (
                <div key={m._id} className="tpl-option-row">
                    <input type="text" className="tpl-input tpl-mono" value={m.key}
                        placeholder="värde (t.ex. 25)"
                        onChange={(e) => setMap(m._id, { key: e.target.value })} />
                    <TplSelect options={accountOptions} value={m.account}
                        placeholder="— konto —"
                        onChange={(v) => setMap(m._id, { account: v })} />
                    <button type="button" className="tpl-icon-btn danger" onClick={() => removeMap(m._id)}>
                        <Trash2 size={14} />
                    </button>
                </div>
            ))}
            <button type="button" className="tpl-btn-outline sm" onClick={addMap}>
                <Plus size={14} /> Lägg till värde
            </button>
        </div>
    );
}

/* ================================================================== *
 * Main component
 * ================================================================== */
function TemplateEditor() {
    const history = useHistory();
    const { companyId, templateId } = useParams();
    const isNew = templateId === 'new';

    // -- Metadata --
    const [templateName, setTemplateName] = useState('');
    const [description, setDescription] = useState('');
    const [templateType, setTemplateType] = useState('expense');
    const [category, setCategory] = useState('ovrigt');
    const [transactionOrigin, setTransactionOrigin] = useState('domestic');
    const [applicableCompanyTypes, setApplicableCompanyTypes] = useState([]);
    const [supportedChartVersions, setSupportedChartVersions] = useState([]);
    const [supportedAccountingRegimes, setSupportedAccountingRegimes] = useState([]);
    const [visibility, setVisibility] = useState('private');

    // -- Body (form state) --
    const [inputs, setInputs] = useState([]);
    const [lines, setLines] = useState([newLineRow('debit'), newLineRow('credit')]);
    const [vatReportCodes, setVatReportCodes] = useState(null);
    const [requiresSupplierVat, setRequiresSupplierVat] = useState(false);
    const [requiresCustomerVat, setRequiresCustomerVat] = useState(false);
    const [sourceCitations, setSourceCitations] = useState([]);

    // -- Editor chrome --
    // 'guided' = the simple builder (new templates start here); 'form'
    // / 'json' = the advanced editor. Existing templates open straight
    // in 'form' — a saved body can't be reduced back to guided choices.
    const [editorMode, setEditorMode] = useState(isNew ? 'guided' : 'form');
    // Latest definition built by the guided form (POST /recipe result),
    // or null while the guided form is incomplete.
    const [guidedDefinition, setGuidedDefinition] = useState(null);
    const [jsonDraft, setJsonDraft] = useState('');
    const [jsonError, setJsonError] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [loadedSource, setLoadedSource] = useState(null);

    // -- Preview modal --
    const [showPreview, setShowPreview] = useState(false);

    const saveAsPrivate = !isNew && loadedSource && loadedSource !== 'tenant';

    /* ---- Load accounts + (optionally) the template ---- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setFetchLoading(true);
            try {
                const res = await axios.get(
                    getApiUrl(`${companyId}/accounting/accounts?scope=company`),
                    { withCredentials: true },
                );
                const mapped = (res.data.accounts || []).map((a) => ({
                    value: a.account_number,
                    label: `${a.account_number} - ${a.account_name}`,
                    accountNumber: a.account_number,
                    accountName: a.account_name,
                    accountType: a.account_type,
                    account_number: a.account_number,
                    account_name: a.account_name,
                    is_base_plan: a.is_base_plan,
                }));
                mapped.sort((x, y) => x.accountNumber.localeCompare(y.accountNumber));
                if (!cancelled) setAccounts(mapped);

                if (!isNew) {
                    const tpl = await getBookkeepingTemplate(companyId, templateId);
                    if (cancelled) return;
                    if (!tpl) throw new Error('Mallen hittades inte.');
                    hydrateFromTemplate(tpl);
                }
            } catch (err) {
                if (!cancelled) setError(toUserErrorMessage(err));
            } finally {
                if (!cancelled) setFetchLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, templateId]);

    function hydrateFromTemplate(tpl) {
        setTemplateName(tpl.template_name || '');
        setDescription(tpl.description || '');
        setTemplateType(tpl.template_type || 'expense');
        setCategory(tpl.category || 'ovrigt');
        setTransactionOrigin(tpl.transaction_origin || 'domestic');
        setApplicableCompanyTypes(Array.isArray(tpl.applicable_company_types)
            ? tpl.applicable_company_types : []);
        setSupportedChartVersions(Array.isArray(tpl.supported_chart_versions)
            ? tpl.supported_chart_versions : []);
        setSupportedAccountingRegimes(Array.isArray(tpl.supported_accounting_regimes)
            ? tpl.supported_accounting_regimes : []);
        setSourceCitations(Array.isArray(tpl.source_citations) ? tpl.source_citations : []);
        setLoadedSource(tpl.source || null);
        setVisibility('private');

        const fs = bodyToFormState(tpl.body);
        setInputs(fs.inputs);
        setLines(fs.lines);
        setVatReportCodes(fs.vatReportCodes);
        setRequiresSupplierVat(fs.requiresSupplierVat);
        setRequiresCustomerVat(fs.requiresCustomerVat);
    }

    /* ---- Assemble the body from current state ---- */
    const formBody = useMemo(() => serializeBody({
        type: templateType,
        transactionOrigin,
        vatReportCodes,
        requiresSupplierVat,
        requiresCustomerVat,
        inputs,
        lines,
    }), [templateType, transactionOrigin, vatReportCodes,
        requiresSupplierVat, requiresCustomerVat, inputs, lines]);

    // The body actually used for preview + save: parsed JSON in JSON
    // mode, the form-assembled body otherwise.
    const effectiveBody = useMemo(() => {
        if (editorMode === 'json') {
            try {
                const parsed = JSON.parse(jsonDraft);
                return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
            } catch {
                return null;
            }
        }
        return formBody;
    }, [editorMode, jsonDraft, formBody]);

    /* ---- Editor-mode switching ---- */
    const switchMode = (target) => {
        if (target === editorMode) return;
        if (target === 'json') {
            setJsonDraft(JSON.stringify(formBody, null, 2));
            setJsonError(null);
            setEditorMode('json');
            return;
        }
        // json -> form: parse and rebuild the form state so the visual
        // editor reflects exactly what the JSON says.
        let parsed;
        try {
            parsed = JSON.parse(jsonDraft);
        } catch (err) {
            setJsonError(err.message);
            return;
        }
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            setJsonError('Body måste vara ett objekt.');
            return;
        }
        const fs = bodyToFormState(parsed);
        setInputs(fs.inputs);
        setLines(fs.lines);
        setVatReportCodes(fs.vatReportCodes);
        setRequiresSupplierVat(fs.requiresSupplierVat);
        setRequiresCustomerVat(fs.requiresCustomerVat);
        if (parsed.type) setTemplateType(parsed.type);
        if (parsed.transaction_origin) setTransactionOrigin(parsed.transaction_origin);
        setEditorMode('form');
    };

    // Guided → advanced handoff. One-way: load the backend-built
    // recipe into the advanced editor so it opens pre-filled with the
    // suggested accounts instead of blank, then switch mode.
    const goAdvanced = () => {
        if (!guidedDefinition) {
            swal('Inget att anpassa än',
                'Fyll i mallens uppgifter i det enkla läget först.', 'info');
            return;
        }
        setTemplateName(guidedDefinition.template_name || '');
        setDescription(guidedDefinition.description || '');
        setTemplateType(guidedDefinition.template_type || 'expense');
        setCategory(guidedDefinition.category || 'ovrigt');
        setTransactionOrigin(guidedDefinition.transaction_origin || 'domestic');
        setApplicableCompanyTypes(guidedDefinition.applicable_company_types || []);
        setSupportedChartVersions(guidedDefinition.supported_chart_versions || []);
        setSupportedAccountingRegimes(guidedDefinition.supported_accounting_regimes || []);
        const fs = bodyToFormState(guidedDefinition.body);
        setInputs(fs.inputs);
        setLines(fs.lines);
        setVatReportCodes(fs.vatReportCodes);
        setRequiresSupplierVat(fs.requiresSupplierVat);
        setRequiresCustomerVat(fs.requiresCustomerVat);
        setEditorMode('form');
    };

    /* ---- Derived warnings (genuine, not legacy) ---- */
    const customAccountWarnings = useMemo(() => {
        if (visibility !== 'shared') return [];
        const numbers = new Set();
        lines.forEach((l) => {
            if (l._mode === 'account' && l.account) numbers.add(l.account);
            if (l._mode === 'account_band') l.band_map.forEach((m) => m.account && numbers.add(m.account));
        });
        return accounts.filter((a) => numbers.has(a.accountNumber) && a.is_base_plan === 0);
    }, [visibility, lines, accounts]);

    /* ---- Validation + save ---- */
    const validate = () => {
        if (!templateName.trim()) {
            swal('Validering misslyckades', 'Mallnamn är obligatoriskt.', 'error');
            return false;
        }
        if (!effectiveBody) {
            swal('Validering misslyckades',
                'JSON-mallen kunde inte tolkas. Kontrollera syntaxen i JSON-läget.', 'error');
            return false;
        }
        if (editorMode === 'form') {
            const names = inputs.map((i) => i.name).filter(Boolean);
            if (names.length !== new Set(names).size) {
                swal('Validering misslyckades', 'Parameternamn måste vara unika.', 'error');
                return false;
            }
            if (inputs.some((i) => !i.name || !i.label)) {
                swal('Validering misslyckades', 'Varje parameter måste ha både namn och etikett.', 'error');
                return false;
            }
        }
        if (!Array.isArray(effectiveBody.lines) || effectiveBody.lines.length < 2) {
            swal('Validering misslyckades',
                'Mallen måste ha minst två bokföringsrader för att verifikatet ska balansera.', 'error');
            return false;
        }
        if (supportedChartVersions.length === 0 || supportedAccountingRegimes.length === 0) {
            swal('Kompatibilitet krävs',
                'Välj minst en kontoplan och en redovisningsregel som mallen stöder.', 'error');
            return false;
        }
        if (visibility === 'shared') {
            const valid = sourceCitations.filter((c) => c && c.url && c.authority);
            if (valid.length === 0) {
                swal('Källa krävs',
                    'Delade mallar måste ange minst en primärkälla (BFN, Skatteverket, Riksdagen eller BAS).',
                    'error');
                return false;
            }
            if (customAccountWarnings.length > 0) {
                swal('Kan inte dela mall',
                    'Mallen använder anpassade konton som inte finns i BAS-kontoplanen. '
                    + 'Endast mallar med BAS-standardkonton kan delas.', 'error');
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        setError(null);

        // Guided mode: the backend already built and validated the
        // whole definition — just persist it as a new private template.
        if (editorMode === 'guided') {
            if (!guidedDefinition) {
                swal('Mallen är inte klar',
                    'Fyll i mallens uppgifter så att förhandsvisningen visas, och försök igen.', 'info');
                return;
            }
            try {
                setIsSaving(true);
                await createBookkeepingTemplate(companyId, {
                    template_name: guidedDefinition.template_name,
                    description: guidedDefinition.description || undefined,
                    template_type: guidedDefinition.template_type,
                    transaction_origin: guidedDefinition.transaction_origin,
                    category: guidedDefinition.category,
                    applicable_company_types: guidedDefinition.applicable_company_types,
                    supported_chart_versions: guidedDefinition.supported_chart_versions,
                    supported_accounting_regimes: guidedDefinition.supported_accounting_regimes,
                    body: guidedDefinition.body,
                    source_citations: [],
                    visibility: 'private',
                });
                history.goBack();
            } catch (err) {
                setError(toUserErrorMessage(err));
            } finally {
                setIsSaving(false);
            }
            return;
        }

        if (!validate()) return;
        const payload = {
            template_name: templateName.trim(),
            description: description.trim() || undefined,
            template_type: effectiveBody.type || templateType,
            transaction_origin: effectiveBody.transaction_origin || transactionOrigin,
            category,
            applicable_company_types: applicableCompanyTypes,
            supported_chart_versions: supportedChartVersions,
            supported_accounting_regimes: supportedAccountingRegimes,
            body: effectiveBody,
            source_citations: sourceCitations.filter((c) => c && c.url && c.authority),
        };
        if (isNew || saveAsPrivate) {
            payload.visibility = (isNew && visibility === 'shared') ? 'shared' : 'private';
        }
        try {
            setIsSaving(true);
            if (isNew || saveAsPrivate) {
                await createBookkeepingTemplate(companyId, payload);
                if (isNew && visibility === 'shared') {
                    await swal('Skickad för granskning',
                        'Din mall har skickats till en administratör för granskning.', 'info');
                }
            } else {
                await updateBookkeepingTemplate(companyId, templateId, payload);
            }
            history.goBack();
        } catch (err) {
            const details = err.response?.data?.details;
            if (Array.isArray(details) && details.length > 0) {
                setError('Mallen kunde inte sparas:\n'
                    + details.map((d) => `• ${d.path || ''}: ${d.message || d}`).join('\n'));
            } else {
                setError(toUserErrorMessage(err));
            }
        } finally {
            setIsSaving(false);
        }
    };

    /* ---- Render ---- */
    if (fetchLoading) {
        return (
            <div className="tpl-editor">
                <div className="tpl-loading">
                    <div className="tpl-spinner" />
                    <p>Laddar mall…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tpl-editor mt-4">
            <div className="tpl-topbar">
                <button type="button" className="tpl-btn-secondary" onClick={() => history.goBack()}
                    disabled={isSaving}>
                    <ArrowLeft size={16} /> Tillbaka
                </button>
                <h2 className="tpl-title">
                    <FileText size={22} />
                    {isNew ? 'Skapa ny mall' : 'Redigera mall'}
                </h2>
                {editorMode === 'guided' ? (
                    <button type="button" className="tpl-btn-secondary"
                        onClick={goAdvanced} disabled={!guidedDefinition}
                        title={guidedDefinition ? 'Finjustera mallen i det avancerade läget'
                            : 'Fyll i mallen först'}>
                        <ListChecks size={16} /> Avancerat
                    </button>
                ) : (
                    <button type="button" className="tpl-btn-secondary"
                        onClick={() => setShowPreview(true)}
                        disabled={!effectiveBody}
                        title={effectiveBody ? '' : 'Rätta JSON-felet först'}>
                        <Eye size={16} /> Förhandsgranska
                    </button>
                )}
                <button type="button" className="tpl-btn-primary" onClick={handleSave}
                    disabled={isSaving || (editorMode === 'guided' && !guidedDefinition)
                        || (editorMode === 'json' && !!jsonError)}>
                    {isSaving ? <span className="tpl-spinner sm" /> : <Save size={16} />}
                    {isNew ? 'Skapa mall' : 'Spara ändringar'}
                </button>
            </div>

            {customAccountWarnings.length > 0 && (
                <Alert variant="warning" className="tpl-alert">
                    <AlertTriangle size={15} />{' '}
                    <strong>Anpassade konton:</strong> mallen använder konton utanför
                    BAS-kontoplanen ({customAccountWarnings.map((a) => a.accountNumber).join(', ')}).
                    Sådana mallar kan inte delas — välj BAS-standardkonton eller spara som privat mall.
                </Alert>
            )}
            {error && (
                <Alert variant="danger" className="tpl-alert" dismissible onClose={() => setError(null)}
                    style={{ whiteSpace: 'pre-line' }}>
                    {error}
                </Alert>
            )}

            <div className="tpl-content">
                {editorMode === 'guided' ? (
                    <GuidedTemplateForm companyId={companyId} accounts={accounts}
                        onChange={setGuidedDefinition} />
                ) : (
                <>
                {/* Section: Grunduppgifter */}
                    <section className="tpl-section">
                        <header className="tpl-section-head">
                            <Settings2 size={18} />
                            <div>
                                <h3>Grunduppgifter</h3>
                                <p>Vad mallen heter och var den hör hemma.</p>
                            </div>
                        </header>
                        <div className="tpl-section-body">
                            <div className="tpl-field">
                                <label className="tpl-label">Mallnamn</label>
                                <input type="text" className="tpl-input" value={templateName}
                                    placeholder="t.ex. Inköp av varor 25 % moms"
                                    onChange={(e) => setTemplateName(e.target.value)} />
                            </div>
                            <div className="tpl-field">
                                <label className="tpl-label">Beskrivning</label>
                                <textarea className="tpl-input tpl-textarea" rows={5} value={description}
                                    placeholder="Beskriv kort när mallen ska användas — t.ex. vilken affärshändelse den bokför och när den passar."
                                    onChange={(e) => setDescription(e.target.value)} />
                            </div>
                            <div className="tpl-grid-3">
                                <div className="tpl-field">
                                    <FieldLabel text="Malltyp"
                                        hint="Vilken sorts affärshändelse mallen bokför." />
                                    <TplSelect options={TEMPLATE_TYPES} value={templateType}
                                        onChange={setTemplateType} />
                                </div>
                                <div className="tpl-field">
                                    <FieldLabel text="Kategori"
                                        hint="Var mallen hamnar i listan när användaren letar efter en mall." />
                                    <TplSelect options={CATEGORIES} value={category}
                                        onChange={setCategory} />
                                </div>
                                <div className="tpl-field">
                                    <FieldLabel text="Transaktionens ursprung"
                                        hint={'Var motparten finns — styr momshanteringen. Välj "Inrikes" för vanliga svenska affärer.'} />
                                    <TplSelect options={TRANSACTION_ORIGINS} value={transactionOrigin}
                                        onChange={setTransactionOrigin} />
                                </div>
                            </div>
                            <div className="tpl-field">
                                <FieldLabel text="Företagsformer mallen gäller"
                                    hint={'Välj "Alla företagsformer" om mallen passar alla — annars kryssa för de specifika. AB = Aktiebolag, EF = Enskild firma, HB = Handelsbolag, KB = Kommanditbolag, EK = Ekonomisk förening, IF = Ideell förening, ST = Stiftelse.'} />
                                <div className="tpl-chips">
                                    <button type="button"
                                        className={`tpl-chip ${applicableCompanyTypes.length === 0 ? 'active' : ''}`}
                                        onClick={() => setApplicableCompanyTypes([])}>
                                        Alla företagsformer
                                    </button>
                                    {COMPANY_TYPE_CODES.map((code) => (
                                        <button type="button" key={code}
                                            title={COMPANY_TYPE_LABELS[code]}
                                            className={`tpl-chip ${applicableCompanyTypes.includes(code) ? 'active' : ''}`}
                                            onClick={() => setApplicableCompanyTypes(
                                                applicableCompanyTypes.includes(code)
                                                    ? applicableCompanyTypes.filter((c) => c !== code)
                                                    : [...applicableCompanyTypes, code],
                                            )}>
                                            {code}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="tpl-field">
                                <FieldLabel text="Kontoplaner mallen stöder"
                                    hint="Mallen valideras mot varje vald kontoplan innan den kan delas eller användas." />
                                <div className="tpl-chips">
                                    {CHART_VERSIONS.map((version) => (
                                        <button type="button" key={version}
                                            className={`tpl-chip ${supportedChartVersions.includes(version) ? 'active' : ''}`}
                                            onClick={() => setSupportedChartVersions(
                                                supportedChartVersions.includes(version)
                                                    ? supportedChartVersions.filter((v) => v !== version)
                                                    : [...supportedChartVersions, version],
                                            )}>
                                            {version}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="tpl-field">
                                <FieldLabel text="Redovisningsregler mallen stöder"
                                    hint="Använd K1, K2 eller K3. Ej tillämpligt gäller företagsformer utan vald regel." />
                                <div className="tpl-chips">
                                    {ACCOUNTING_REGIMES.map(({ value, label }) => (
                                        <button type="button" key={value ?? 'none'}
                                            className={`tpl-chip ${supportedAccountingRegimes.includes(value) ? 'active' : ''}`}
                                            onClick={() => setSupportedAccountingRegimes(
                                                supportedAccountingRegimes.includes(value)
                                                    ? supportedAccountingRegimes.filter((v) => v !== value)
                                                    : [...supportedAccountingRegimes, value],
                                            )}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {isNew && (
                                <div className="tpl-field">
                                    <label className="tpl-label">Synlighet</label>
                                    <label className="tpl-radio">
                                        <input type="radio" name="vis" checked={visibility === 'private'}
                                            onChange={() => setVisibility('private')} />
                                        Privat — endast mitt företag
                                    </label>
                                    <label className="tpl-radio">
                                        <input type="radio" name="vis" checked={visibility === 'shared'}
                                            onChange={() => setVisibility('shared')} />
                                        Dela med alla — kräver godkännande
                                    </label>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Mode tabs */}
                    <div className="tpl-tabs">
                        <button type="button"
                            className={`tpl-tab ${editorMode === 'form' ? 'active' : ''}`}
                            onClick={() => switchMode('form')}>
                            <ListChecks size={15} /> Formulär
                        </button>
                        <button type="button"
                            className={`tpl-tab ${editorMode === 'json' ? 'active' : ''}`}
                            onClick={() => switchMode('json')}>
                            <Code size={15} /> JSON (avancerat)
                        </button>
                    </div>

                    {editorMode === 'form' ? (
                        <>
                            {/* Section: Parametrar */}
                            <section className="tpl-section">
                                <header className="tpl-section-head">
                                    <ListChecks size={18} />
                                    <div>
                                        <h3>Parametrar</h3>
                                        <p>Uppgifterna användaren fyller i när mallen används.</p>
                                    </div>
                                </header>
                                <div className="tpl-section-body">
                                    {inputs.length === 0 ? (
                                        <p className="tpl-empty">Inga parametrar ännu. Lägg till den första parametern nedan.</p>
                                    ) : (
                                        <div className="tpl-table-wrap">
                                            <table className="tpl-table tpl-table--params">
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <span>Namn</span>
                                                            <InfoDot hint="Namnet du använder i beloppsformler. Endast bokstäver, siffror och understreck." />
                                                        </th>
                                                        <th>
                                                            <span>Etikett</span>
                                                            <InfoDot hint="Texten som visas för användaren när mallen används." />
                                                        </th>
                                                        <th>
                                                            <span>Typ</span>
                                                            <InfoDot hint="Vilken sorts uppgift parametern är — styr vilket inmatningsfält användaren får." />
                                                        </th>
                                                        <th>
                                                            <span>Exempelvärde</span>
                                                            <InfoDot hint="Ett realistiskt värde som används för att förhandsvisa verifikatet." />
                                                        </th>
                                                        <th className="tpl-td-center">
                                                            <span>Oblig.</span>
                                                            <InfoDot hint="Måste användaren fylla i parametern?" />
                                                        </th>
                                                        <th aria-label="Åtgärder" />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {inputs.map((inp) => (
                                                        <ParameterRow key={inp._id} input={inp}
                                                            onChange={(next) => setInputs(inputs.map(
                                                                (x) => (x._id === inp._id ? next : x)))}
                                                            onRemove={() => setInputs(
                                                                inputs.filter((x) => x._id !== inp._id))} />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <button type="button" className="tpl-btn-outline"
                                        onClick={() => setInputs([...inputs, newInputRow()])}>
                                        <Plus size={16} /> Lägg till parameter
                                    </button>
                                </div>
                            </section>

                            {/* Section: Bokföringsrader */}
                            <section className="tpl-section">
                                <header className="tpl-section-head">
                                    <FileText size={18} />
                                    <div>
                                        <h3>Bokföringsrader</h3>
                                        <p>Verifikatets rader. Debet och kredit måste balansera.</p>
                                    </div>
                                </header>
                                <div className="tpl-section-body">
                                    <div className="tpl-table-wrap">
                                        <table className="tpl-table tpl-table--lines">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <span>Kontoläge</span>
                                                        <InfoDot hint="Hur kontot bestäms — Fast konto: alltid samma konto. Från parameter: kontot tas från en kontoväljar- eller listvalsparameter. Kontoband: en styrparameters värde avgör kontot." />
                                                    </th>
                                                    <th>
                                                        <span>Konto</span>
                                                        <InfoDot hint="Kontot raden bokförs på — eller parametern som avgör kontot." />
                                                    </th>
                                                    <th>
                                                        <span>Debet</span>
                                                        <InfoDot hint="Beloppsformel för debetsidan, t.ex. belopp.net. Klicka + i kreditkolumnen för att flytta beloppet dit." />
                                                    </th>
                                                    <th>
                                                        <span>Kredit</span>
                                                        <InfoDot hint="Beloppsformel för kreditsidan." />
                                                    </th>
                                                    <th aria-label="Åtgärder" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lines.map((ln) => (
                                                    <LineRow key={ln._id} line={ln}
                                                        inputs={inputs} accounts={accounts}
                                                        onChange={(next) => setLines(lines.map(
                                                            (x) => (x._id === ln._id ? next : x)))}
                                                        onRemove={() => {
                                                            if (lines.length <= 2) {
                                                                swal('Kan inte ta bort',
                                                                    'En mall behöver minst två rader.', 'warning');
                                                                return;
                                                            }
                                                            setLines(lines.filter((x) => x._id !== ln._id));
                                                        }} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button type="button" className="tpl-btn-outline"
                                        onClick={() => setLines([...lines, newLineRow('debit')])}>
                                        <Plus size={16} /> Lägg till rad
                                    </button>
                                </div>
                            </section>
                        </>
                    ) : (
                        /* JSON mode */
                        <section className="tpl-section">
                            <header className="tpl-section-head">
                                <Code size={18} />
                                <div>
                                    <h3>Mall-body (JSON)</h3>
                                    <p>Redigera body-objektet direkt. Valideras mot samma schema som formuläret.</p>
                                </div>
                            </header>
                            <div className="tpl-section-body">
                                <textarea
                                    className={`tpl-json ${jsonError ? 'has-error' : ''}`}
                                    value={jsonDraft}
                                    spellCheck={false}
                                    onChange={(e) => {
                                        setJsonDraft(e.target.value);
                                        try {
                                            JSON.parse(e.target.value);
                                            setJsonError(null);
                                        } catch (err) {
                                            setJsonError(err.message);
                                        }
                                    }} />
                                {jsonError && (
                                    <Alert variant="danger" className="tpl-alert mt-2 mb-0">
                                        <AlertTriangle size={14} /> JSON-fel: {jsonError}
                                    </Alert>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Section: Källor — hidden for now (SHOW_CITATIONS).
                        Citation data still round-trips on the model; kept
                        for a future AI-assisted use. */}
                    {SHOW_CITATIONS && (
                    <section className="tpl-section">
                        <header className="tpl-section-head">
                            <BookOpen size={18} />
                            <div>
                                <h3>Källor</h3>
                                <p>
                                    {visibility === 'shared'
                                        ? 'Delade mallar måste ange minst en primärkälla.'
                                        : 'Valfritt för privata mallar.'}
                                </p>
                            </div>
                        </header>
                        <div className="tpl-section-body">
                            {sourceCitations.map((c, idx) => (
                                <div key={idx} className="tpl-card">
                                    <div className="tpl-card-head">
                                        <span className="tpl-card-index">Källa {idx + 1}</span>
                                        <button type="button" className="tpl-icon-btn danger"
                                            onClick={() => setSourceCitations(
                                                sourceCitations.filter((_, i) => i !== idx))}>
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                    <div className="tpl-grid-2">
                                        <div className="tpl-field">
                                            <label className="tpl-label">Myndighet</label>
                                            <TplSelect options={AUTHORITIES} value={c.authority || 'BAS'}
                                                onChange={(v) => {
                                                    const next = [...sourceCitations];
                                                    next[idx] = { ...next[idx], authority: v };
                                                    setSourceCitations(next);
                                                }} />
                                        </div>
                                        <div className="tpl-field">
                                            <label className="tpl-label">Dokument</label>
                                            <input type="text" className="tpl-input" value={c.document || ''}
                                                placeholder="t.ex. BFNAR 2016:10 (K2)"
                                                onChange={(e) => {
                                                    const next = [...sourceCitations];
                                                    next[idx] = { ...next[idx], document: e.target.value };
                                                    setSourceCitations(next);
                                                }} />
                                        </div>
                                    </div>
                                    <div className="tpl-grid-2">
                                        <div className="tpl-field">
                                            <label className="tpl-label">Avsnitt (valfritt)</label>
                                            <input type="text" className="tpl-input" value={c.section || ''}
                                                placeholder="t.ex. 9 kap 5 §"
                                                onChange={(e) => {
                                                    const next = [...sourceCitations];
                                                    next[idx] = { ...next[idx], section: e.target.value || undefined };
                                                    setSourceCitations(next);
                                                }} />
                                        </div>
                                        <div className="tpl-field">
                                            <label className="tpl-label">URL</label>
                                            <input type="url" className="tpl-input" value={c.url || ''}
                                                placeholder="https://www.bfn.se/…"
                                                onChange={(e) => {
                                                    const next = [...sourceCitations];
                                                    next[idx] = { ...next[idx], url: e.target.value };
                                                    setSourceCitations(next);
                                                }} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button type="button" className="tpl-btn-outline"
                                onClick={() => setSourceCitations([
                                    ...sourceCitations, { authority: 'BAS', document: '', url: '' },
                                ])}>
                                <Plus size={16} /> Lägg till källa
                            </button>
                        </div>
                    </section>
                    )}
                </>
                )}
                </div>

                <TemplatePreviewModal
                    show={showPreview}
                    onClose={() => setShowPreview(false)}
                    companyId={companyId}
                    body={effectiveBody}
                    accounts={accounts}
                    contract={{
                        applicableCompanyTypes,
                        supportedChartVersions,
                        supportedAccountingRegimes,
                    }} />
        </div>
    );
}

export default TemplateEditor;
